/**
 * "Pick one of N" result-grid pattern — a candidate list with a built-in
 * loading→result cycle: a skeleton grid while `loading`, a selectable tile
 * grid once `items` resolve, a ring-highlighted selected tile, and a
 * regenerate affordance. Domain-agnostic — no knowledge of what produced
 * the candidates (AI generation, a search, anything); the caller owns
 * fetching/generating `items` and reacts to `select`/`regenerate`.
 */
import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { parseAspectRatio } from '@schmancy/utils'
import { css, html } from 'lit'
import { property, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import '../button/button'
import '../card/card'
import '../icons/icon'
import '../skeleton/skeleton'
import '../typography/typography'

export interface ResultGridItem {
	id: string
	imageUrl: string
	/** The tile's caption — rendered under the image and used as its accessible name. */
	label?: string
}

export type ResultGridSelectEvent = CustomEvent<{ id: string }>

/**
 * @element schmancy-result-grid
 * @fires select - `{ id }` — a tile was picked.
 * @fires regenerate - fired when the regenerate affordance is activated. The caller owns re-running generation and updating `items`/`loading`.
 * @slot trailing - one consumer-owned cell at the end of the same grid, at the same `ratio` — the door into a set the user can add to.
 */
@schmancyElement('schmancy-result-grid')
export class SchmancyResultGrid extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	]

	@property({ attribute: false }) items: ResultGridItem[] = []
	@property({ type: Boolean }) loading = false
	@property({ type: String }) selected: string | null = null
	@property({ type: Number, attribute: 'skeleton-count' }) skeletonCount = 4
	/** Tile aspect ratio as "W:H". */
	@property({ type: String }) ratio = '1:1'
	/**
	 * How a candidate's image meets its tile: `cover` crops it to fill the
	 * frame, `contain` letterboxes the whole image inside it. A chooser whose
	 * candidates do not all share `ratio` — a portrait poster among square
	 * ones, an operator's own upload among generated art — needs `contain`,
	 * because a cropped thumbnail misrepresents the shape of the thing being
	 * picked.
	 */
	@property({ type: String }) fit: 'cover' | 'contain' = 'cover'
	@property({ type: Boolean }) regenerable = true
	@property({ type: String, attribute: 'regenerate-label' }) regenerateLabel = 'Regenerate'
	/** Accessible name for the tile group and the loading announcement — e.g. "poster concepts". */
	@property({ type: String }) label = 'options'

	/**
	 * Whether the consumer has slotted a `trailing` cell. Read off the slot's
	 * own assignment rather than taken as a prop, so a grid nobody adds to
	 * renders byte-for-byte what it rendered before the slot existed — the
	 * wrapper below is `hidden` until something lands in it, and a display:none
	 * box is not a grid item.
	 */
	@state() private _hasTrailing = false

	override render() {
		const [rw, rh] = parseAspectRatio(this.ratio)

		if (this.loading) {
			return html`
				<div
					role="status"
					aria-busy="true"
					aria-label="Generating ${this.label}"
					class="grid grid-cols-2 gap-3 sm:grid-cols-4"
				>
					${repeat(
						Array.from({ length: this.skeletonCount }, (_, i) => i),
						i => i,
						() =>
							html`<schmancy-skeleton
								class="w-full rounded-xl"
								style="aspect-ratio: ${rw} / ${rh}"
							></schmancy-skeleton>`,
					)}
				</div>
			`
		}

		return html`
			<div
				class=${this.classMap({
					'flex flex-col gap-3': true,
					hidden: this.items.length === 0 && !this._hasTrailing,
				})}
			>
				<div role="group" aria-label=${this.label} class="grid grid-cols-2 gap-3 sm:grid-cols-4">
					${repeat(
						this.items,
						item => item.id,
						(item, i) => {
							const isSelected = item.id === this.selected
							return html`
								<schmancy-card
									type=${isSelected ? 'filled' : 'outlined'}
									interactive
									role="button"
									class=${this.classMap({
										'rounded-xl overflow-hidden': true,
										'ring-2 ring-primary-default': isSelected,
									})}
									aria-pressed=${isSelected ? 'true' : 'false'}
									aria-label=${`${item.label ?? `Option ${i + 1}`}${isSelected ? ', selected' : ''}`}
									@click=${() =>
										this.dispatchEvent(
											new CustomEvent('select', { detail: { id: item.id }, bubbles: true, composed: true }),
										)}
								>
									<div class="w-full" style="aspect-ratio: ${rw} / ${rh}">
										<img
											src=${item.imageUrl}
											alt=""
											class=${this.classMap({
												'h-full w-full': true,
												'object-cover': this.fit === 'cover',
												'object-contain': this.fit === 'contain',
											})}
										/>
									</div>
									${when(
										item.label,
										() => html`
											<schmancy-typography type="label" token="sm" align="center" class="block px-2 py-1.5">
												${item.label}
											</schmancy-typography>
										`,
									)}
								</schmancy-card>
							`
						},
					)}
					<!-- The door into a set the user can add to, in the grid rather than
					     beside it: a control standing outside stops reading as part of the
					     set the moment the set has anything in it. The ratio is the
					     primitive's because only it knows one; everything inside the cell
					     is the consumer's. -->
					<div
						class=${this.classMap({ 'grid w-full min-h-0 self-start overflow-hidden': true, hidden: !this._hasTrailing })}
						style="aspect-ratio: ${rw} / ${rh}"
					>
						<slot
							name="trailing"
							@slotchange=${(e: Event) => {
								this._hasTrailing = (e.target as HTMLSlotElement).assignedElements().length > 0
							}}
						></slot>
					</div>
				</div>
				${when(
					this.regenerable && this.items.length > 0,
					() => html`
						<schmancy-button
							variant="text"
							class="self-start"
							@click=${() => this.dispatchEvent(new CustomEvent('regenerate', { bubbles: true, composed: true }))}
						>
							<schmancy-icon slot="prefix" aria-hidden="true">refresh</schmancy-icon>
							${this.regenerateLabel}
						</schmancy-button>
					`,
				)}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-result-grid': SchmancyResultGrid
	}
}
